/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CreateApiKeyPayload } from '../models/CreateApiKeyPayload';
import type { DeleteApiKeyPayload } from '../models/DeleteApiKeyPayload';
import type { LumonicApiKeyCreateResponse } from '../models/LumonicApiKeyCreateResponse';
import type { LumonicApiKeyReadSafe } from '../models/LumonicApiKeyReadSafe';
import type { RevokeApiKeyPayload } from '../models/RevokeApiKeyPayload';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ApiKeysService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Create Api Key
     * Create a new API key for a cluster
     *
     * Requires cluster admin permissions.
     * The API key is only shown once in the response - save it securely.
     * @param clusterId
     * @param requestBody
     * @returns LumonicApiKeyCreateResponse Successful Response
     * @throws ApiError
     */
    public createApiKey(
        clusterId: string,
        requestBody: CreateApiKeyPayload,
    ): CancelablePromise<LumonicApiKeyCreateResponse> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/create-api-key',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * List Cluster Api Keys
     * List all API keys for a cluster
     *
     * Requires cluster admin permissions.
     * Returns all keys including revoked ones for audit purposes.
     * Keys are masked for security - only last 4 characters visible.
     * @param clusterId
     * @returns LumonicApiKeyReadSafe Successful Response
     * @throws ApiError
     */
    public listClusterApiKeys(
        clusterId: string,
    ): CancelablePromise<Array<LumonicApiKeyReadSafe>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/list-api-keys',
            path: {
                'cluster_id': clusterId,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Revoke Api Key
     * Revoke an API key (soft delete)
     *
     * Requires cluster admin permissions.
     * Once revoked, the key can no longer be used for authentication.
     * Keeps the key in the database for audit purposes.
     * @param clusterId
     * @param requestBody
     * @returns LumonicApiKeyReadSafe Successful Response
     * @throws ApiError
     */
    public revokeApiKey(
        clusterId: string,
        requestBody: RevokeApiKeyPayload,
    ): CancelablePromise<LumonicApiKeyReadSafe> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/revoke-api-key',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

    /**
     * Delete Api Key
     * Permanently delete an API key (hard delete)
     *
     * Requires cluster admin permissions.
     * Use this to remove keys that were created by accident.
     * This action cannot be undone.
     * @param clusterId
     * @param requestBody
     * @returns void
     * @throws ApiError
     */
    public deleteApiKey(
        clusterId: string,
        requestBody: DeleteApiKeyPayload,
    ): CancelablePromise<void> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/clusters/{cluster_id}/delete-api-key',
            path: {
                'cluster_id': clusterId,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                422: `Validation Error`,
            },
        });
    }

}
